import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ActionButton } from "@/components/shared";

const REASON_OPTIONS = [
  { code: "already_satisfied",           label: "Already satisfied by another engagement" },
  { code: "duplicate_workstream",        label: "Duplicate estimate / workstream" },
  { code: "service_no_longer_required",  label: "Service no longer required" },
  { code: "replaced_by_another_estimate",label: "Replaced by another estimate" },
  { code: "created_in_error",            label: "Created in error" },
  { code: "other",                       label: "Other" },
];

/**
 * ResolveEstimateObligationDialog
 *
 * Staff action to terminalize an obsolete engagement + its linked Estimate
 * through the existing governed lifecycle paths.
 *
 * Props
 *   open                  boolean
 *   onClose               () => void
 *   clientId              string
 *   engagementId          string   obsolete engagement internal ID
 *   engagementNumber      string   for display (ENG-XXXXXX)
 *   estimateNumber        string   for display (EST-XXXXXX), optional
 *   onResolved            () => void  called after successful resolution
 */
export function ResolveEstimateObligationDialog({
  open,
  onClose,
  clientId,
  engagementId,
  engagementNumber,
  estimateNumber,
  onResolved,
}) {
  const navigate = useNavigate();
  const [reasonCode, setReasonCode] = useState("");
  const [notes, setNotes] = useState("");
  const [survivingEngId, setSurvivingEngId] = useState("");
  const [activeEngs, setActiveEngs] = useState([]);
  const [saving, setSaving] = useState(false);

  // Reset state each time the dialog opens
  useEffect(() => {
    if (!open) return;
    setReasonCode(""); setNotes(""); setSurvivingEngId(""); setActiveEngs([]);
    if (!clientId) return;
    // Fetch active engagements for the surviving-engagement selector
    api.get(`/client-success/engagements`, { params: { client_id: clientId, status: "active,signed,generated,draft,sent,viewed", limit: 30 } })
      .then(({ data }) => {
        const engs = (data.engagements || data.items || data || []).filter(
          (e) => e.id !== engagementId && !["cancelled", "superseded", "declined"].includes(e.engagement_status),
        );
        setActiveEngs(engs);
      })
      .catch(() => setActiveEngs([]));
  }, [open, clientId, engagementId]);

  const notesRequired = reasonCode === "other";
  const canSubmit = !!reasonCode && (!notesRequired || notes.trim().length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        reason_code: reasonCode,
        notes: notes.trim() || null,
        surviving_engagement_id: survivingEngId || null,
      };
      await api.post(`/engagements/${engagementId}/resolve-estimate-obligation`, payload);
      toast.success("Estimate obligation resolved", {
        description: `${engagementNumber} has been retired and the stale estimate obligation cleared.`,
      });
      onResolved?.();
      onClose?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Guarded response — staff needs a different workflow
      if (e.response?.status === 409 && detail?.code === "signed_engagement_guard") {
        toast.error("Signed engagement — amendment required", {
          description: "This engagement has been signed. Use Amend Signed from the Engagement Letters tab to create a governed replacement.",
          duration: 8000,
        });
        onClose?.();
        navigate(`/client-workspace/${clientId}?tab=engagements`);
        return;
      }
      if (e.response?.status === 409 && detail?.code === "sent_engagement_guard") {
        toast.error("Sent engagement — withdraw required", {
          description: "This engagement letter has been sent. Use Withdraw & Revise from the Engagement Letters tab.",
          duration: 8000,
        });
        onClose?.();
        navigate(`/client-workspace/${clientId}?tab=engagements`);
        return;
      }
      toast.error(formatApiErrorDetail(detail) || "Resolution failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="resolve-obligation-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Icons.AlertTriangle className="h-4.5 w-4.5 text-amber-500 shrink-0" />
            Resolve Estimate Obligation
          </DialogTitle>
          <DialogDescription>
            Retire the stale estimate obligation for{" "}
            <span className="font-semibold text-slate-700">{engagementNumber}</span>
            {estimateNumber && (
              <> · <span className="font-semibold text-slate-700">{estimateNumber}</span></>
            )}{" "}
            using the governed cancellation path.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Reason selector */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">
              Reason <span className="text-rose-500">*</span>
            </label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger data-testid="resolve-reason-select" className={!reasonCode ? "border-slate-300" : "border-blue-400"}>
                <SelectValue placeholder="Select a reason…" />
              </SelectTrigger>
              <SelectContent>
                {REASON_OPTIONS.map((r) => (
                  <SelectItem key={r.code} value={r.code} data-testid={`resolve-reason-${r.code}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Surviving engagement — shown for all reasons but labeled as optional */}
          {reasonCode && (
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">
                Existing engagement that supersedes this scope
                {reasonCode === "already_satisfied" ? (
                  <span className="ml-1 text-amber-600 font-semibold">— recommended</span>
                ) : (
                  <span className="ml-1 text-slate-400">(optional)</span>
                )}
              </label>
              {activeEngs.length > 0 ? (
                <Select value={survivingEngId} onValueChange={setSurvivingEngId}>
                  <SelectTrigger data-testid="resolve-surviving-eng-select">
                    <SelectValue placeholder="Select surviving engagement…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {activeEngs.map((e) => (
                      <SelectItem key={e.id} value={e.id} data-testid={`resolve-surviving-eng-${e.id?.slice(0, 8)}`}>
                        {e.engagement_number || e.id?.slice(0, 8)}
                        {e.title && <span className="text-slate-500 ml-1">· {e.title.slice(0, 40)}</span>}
                        <span className="ml-1 text-[10px] text-slate-400">({e.engagement_status})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs text-slate-400 py-1">No other active engagements found for this client.</p>
              )}
            </div>
          )}

          {/* Notes — required for "other", optional otherwise */}
          {reasonCode && (
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">
                Notes
                {notesRequired ? (
                  <span className="text-rose-500 ml-1">* required</span>
                ) : (
                  <span className="text-slate-400 ml-1">(optional)</span>
                )}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={notesRequired ? "Describe the specific reason…" : "Additional context (optional)"}
                rows={3}
                data-testid="resolve-notes"
                className={`w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 ${notesRequired && !notes.trim() ? "border-amber-400 bg-amber-50/50" : "border-slate-300"}`}
              />
            </div>
          )}

          {/* Warning notice */}
          {reasonCode && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-start gap-2.5 text-xs text-amber-800" data-testid="resolve-warning-notice">
              <Icons.AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-500" />
              <span>
                This action will cancel <strong>{engagementNumber}</strong> and supersede its linked estimate.
                Paid invoices and other engagements remain untouched.
                All history is preserved. This cannot be undone without manual remediation.
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <ActionButton variant="secondary" onClick={onClose} data-testid="resolve-cancel-btn">
            Cancel
          </ActionButton>
          <ActionButton
            loading={saving}
            disabled={!canSubmit}
            onClick={handleSubmit}
            data-testid="resolve-submit-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            Resolve Obligation
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
